<template>
  <v-card width="100%" class="fill-height pa-4 overflow-visible">
    <RDPDataTable
      ref="dataTable"
      :headers="headers"
      :filter="filter"
      :get-row-class-callback="getRowClassCallback"
      :search-items-callback="searchTicketFare"
      :get-row-click-label-callback="getRowClickLabel"
      i18n-group-key="userAccountPage.ticketFareTab"
      data-cy="user-account-ticket-fare-list"
    >
      <template #head-panel.start>
        <v-col cols="12" md="3" :style="filterStyle">
          <v-select
            v-model="filter.stateIds"
            :items="ticketFareStateItems"
            chips
            :label="$t(`userAccountPage.ticketFareTab.ticketFareFilter`)"
            data-cy="user-account-ticket-fare-filter"
            multiple
            clearable
            variant="underlined"
            @update:model-value="searchTicketFare"
          >
            <template #chip="{ item, index }">
              <v-chip v-if="index === 0">
                <span>{{ item.title }}</span>
                <span v-if="filter.stateIds && filter.stateIds.length > 1" class="text-grey text-caption ml-1">{{
                  $t('multiselectOthers', {
                    selectionLength: filter.stateIds.length - 1,
                  })
                }}</span>
              </v-chip>
            </template>
          </v-select>
        </v-col>
      </template>
      <template #item.customerProfile.displayName="{ item }">
        {{ getCustomerProfileDisplayName(item.customerProfile) }}
      </template>
      <template #item.tariffProfile.displayName="{ item }">
        {{ getTariffProfileDisplayName(item.tariffProfile) }}
      </template>
      <template #item.zone.displayName="{ item }">
        {{ getZonesDisplayName(item.zones) }}
      </template>
    </RDPDataTable>
  </v-card>
</template>

<script lang="ts">
import { Component, Ref, Vue } from 'vue-facing-decorator';
import { DATA_TABLE_HEADER_TYPES } from '@/components/common/RDPDataTable/DataTableHelper';
import customerService from '@/services/api/customerService';
import RDPDataTable from '@/components/common/RDPDataTable/RDPDataTable.vue';
import {
  CustomerProfileInfoVm,
  CustomerTicketFareListResponse,
  TariffProfileInfoVm,
  TicketFareDto,
  TicketFareStateEnum,
  TicketStateListQuery,
  ZoneDto,
} from '@/api';
import ticketFareStateService from '@/services/api/ticketFareStateService';
import CustomerModule from '@/store/modules/customer';
import { RDPDataTableInterface } from '@/components/common/RDPDataTable/RDPDataTableInterfaces';
import { getCustomerProfileDisplayName, getTariffProfileDisplayName, getZoneDisplayName } from '@/utils/i18n';
import { TABLE_TAB_FILTER_STYLES } from '@/constants/Constants';

const DEFAULT_SORT_BY_COLUMN = 'validTo';
const DEFAULT_SORT_DESC = true;

@Component({
  components: {
    RDPDataTable,
  },
})
export default class TickerFareTab extends Vue {
  @Ref()
  readonly dataTable!: RDPDataTableInterface;

  customerModule = CustomerModule;

  filter: TicketStateListQuery = {
    stateIds: [],
    sortBy: DEFAULT_SORT_BY_COLUMN,
    sortDesc: DEFAULT_SORT_DESC,
  };
  headers = [
    {
      text: 'state.displayName',
      applyRowClass: true,
      sortable: false,
    },
    {
      text: 'validFrom',
      type: DATA_TABLE_HEADER_TYPES.DATE,
    },
    {
      text: 'validTo',
      type: DATA_TABLE_HEADER_TYPES.DATE,
    },
    {
      text: 'customerProfile.displayName',
    },
    {
      text: 'zone.displayName',
      transform: (ticketFare: TicketFareDto) => ticketFare.zones.map(zone => zone.displayName).join(', '),
      sortable: false,
    },
    {
      text: 'tariffProfile.displayName',
    },
    {
      text: 'price',
      type: DATA_TABLE_HEADER_TYPES.PRICE,
    },
    { text: 'card.cardNumber', type: DATA_TABLE_HEADER_TYPES.CARD_NUMBER },
    'card.state.displayName',
  ];
  ticketFareStateList: TicketFareStateEnum[] = [];

  async created() {
    this.ticketFareStateList = await ticketFareStateService.getTicketFareStateList();
  }

  get ticketFareStateItems() {
    return this.ticketFareStateList.map((ticketFareState: TicketFareStateEnum) => ({
      title: ticketFareState.displayName,
      value: ticketFareState.name,
    }));
  }

  get filterStyle() {
    return this.$vuetify.display.mobile ? '' : TABLE_TAB_FILTER_STYLES;
  }

  public async searchTicketFare(): Promise<CustomerTicketFareListResponse> {
    return customerService.getCustomerTicketFareList(this.customerModule.currentlySelectedCustomerId, this.filter);
  }

  getRowClassCallback(item: { state: { code: string } }) {
    return `ticket-fare-${item.state.code}`;
  }

  /**
   * This is called from UserAccountPage.vue
   */
  refresh() {
    this.dataTable.refresh();
  }

  getCustomerProfileDisplayName(profile: CustomerProfileInfoVm | undefined): string {
    return getCustomerProfileDisplayName(profile?.id);
  }

  getTariffProfileDisplayName(profile: TariffProfileInfoVm | undefined): string {
    return getTariffProfileDisplayName(profile?.id);
  }

  getZonesDisplayName(zones: ZoneDto[] | undefined): string {
    return zones ? zones.map(zone => getZoneDisplayName(zone?.id)).join(', ') : '';
  }

  getRowClickLabel(item: TicketFareDto) {
    return this.$t('rdpDataTable.openRow', {
      label: `${item.state.displayName} - ${item.customerProfile.name} - ${item.tariffProfile.name}`,
    });
  }
}
</script>

<style>
.ticket-fare-EXPIRED {
  background-color: #b7e2f1 !important;
}

.ticket-fare-FUTURE {
  background-color: #fff9c4 !important;
}

.ticket-fare-CANCELLED {
  background-color: #ffcdd2 !important;
}

.ticket-fare-ACTIVE {
  background-color: #c7ffc9 !important;
}
</style>
